import React, {useEffect} from 'react';
  import {Link} from "react-router-dom";
  import {useApi} from "../../providers/ApiProvider";
  import {useBreadcrumbs} from "../../providers/BreadcrumbsProvider";
  import BusinessesModel from "../../models/BusinessesModel";
  import SimpleCardContainer from "../ui/SimpleCardContainer";
  import {useTranslation} from "react-i18next";
  import {useTranslation as useMyTranslation} from "../../providers/TranslationProvider";

  function Dashboard() {
      const {language} = useMyTranslation();
      const {t} = useTranslation();
      const breadcrumbs = useBreadcrumbs();
      const apiContext = useApi();
      const businessesModel = new BusinessesModel(apiContext.api);

      const defaultDateStart = (new Date(Date.now() - 33 * 24 * 60 * 60 * 1000)).toISOString().slice(0, 10);
      const defaultDateEnd = (new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)).toISOString().slice(0, 10);

      const [dateStart, setDateStart] = React.useState(defaultDateStart);
      const [dateEnd, setDateEnd] = React.useState(defaultDateEnd);
      const [data, setData] = React.useState(null);
      const [compareMode, setCompareMode] = React.useState('previous_period');
      const [loading, setLoading] = React.useState(true);

      const fetchData = (start, end, mode) => {
          setLoading(true);
          businessesModel.dashboardOverview(start, end, mode || compareMode).then((response) => {
              if (response !== false) {
                  setData(response);
              }
          }).finally(() => {
              setLoading(false);
          });
      };

      useEffect(() => {
          breadcrumbs.update({
              title: t('Dashboard'),
              breadcrumbs: [{label: t('Dashboard')}]
          });
          document.title = t('Dashboard') + ' | LocalBoost';
          fetchData(dateStart, dateEnd, compareMode);
      }, [language]);

      useEffect(() => {
          fetchData(dateStart, dateEnd, compareMode);
      }, [dateStart, dateEnd, compareMode]);

      const calcDiff = (current, previous) => {
          if (previous === 0 && current === 0) return 0;
          if (previous > 0) return Math.round(((current - previous) / previous) * 10000) / 100;
          return 100;
      };

      const renderTrend = (current, previous, size) => {
          const diff = calcDiff(current, previous);
          const cls = size === 'lg' ? 'fs-3' : 'fs-2';
          if (diff > 0) {
              return (<span className={"text-success " + cls}><i className="ti ti-trending-up me-1"></i>+{diff}%</span>);
          }
          if (diff < 0) {
              return (<span className={"text-danger " + cls}><i className="ti ti-trending-down me-1"></i>{diff}%</span>);
          }
          return (<span className={"text-muted " + cls}><i className="ti ti-minus me-1"></i>0%</span>);
      };

      const getTitle = (biz) => {
          if (biz.data && biz.data.title) return biz.data.title;
          return biz.name || 'Standort #' + biz.id;
      };

      const getAddress = (biz) => {
          if (biz.data && biz.data.storefrontAddress) {
              const addr = biz.data.storefrontAddress;
              const parts = [];
              if (addr.addressLines) parts.push(addr.addressLines.join(', '));
              if (addr.postalCode) parts.push(addr.postalCode);
              if (addr.locality) parts.push(addr.locality);
              return parts.join(', ');
          }
          return '';
      };

      const setPreset = (days) => {
          const end = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000);
          const start = new Date(end.getTime() - days * 24 * 60 * 60 * 1000);
          setDateStart(start.toISOString().slice(0, 10));
          setDateEnd(end.toISOString().slice(0, 10));
      };

      const kpiCards = data ? [
          {
              icon: 'ti-eye',
              label: t('Impressionen'),
              value: data.totals.impressions,
              prev: data.totals.impressions_prev,
              color: 'primary',
          },
          {
              icon: 'ti-world',
              label: t('Website-Klicks'),
              value: data.totals.website_clicks,
              prev: data.totals.website_clicks_prev,
              color: 'success',
          },
          {
              icon: 'ti-phone',
              label: t('Anrufe'),
              value: data.totals.calls,
              prev: data.totals.calls_prev,
              color: 'warning',
          },
          {
              icon: 'ti-map-pin',
              label: t('Wegbeschreibungen'),
              value: data.totals.directions,
              prev: data.totals.directions_prev,
              color: 'info',
          },
      ] : [];

      return (
          <>
              <div className="row">
                  <div className="col-12 mb-3">
                      <div className="card border-0 shadow-sm mb-0">
                          <div className="card-body py-3">
                              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                                  <h4 className="fw-semibold mb-0">{t('Dashboard-Übersicht')}</h4>
                                  <div className="d-flex flex-wrap align-items-center gap-2">
                                      <div className="btn-group btn-group-sm">
                                          <button className="btn btn-outline-primary" onClick={() => setPreset(7)}>7 {t('Tage')}</button>
                                          <button className="btn btn-outline-primary" onClick={() => setPreset(14)}>14 {t('Tage')}</button>
                                          <button className="btn btn-outline-primary" onClick={() => setPreset(30)}>30 {t('Tage')}</button>
                                          <button className="btn btn-outline-primary" onClick={() => setPreset(90)}>90 {t('Tage')}</button>
                                      </div>
                                      <div className="d-flex align-items-center gap-2">
                                          <input type="date" className="form-control form-control-sm" value={dateStart} onChange={(e) => setDateStart(e.target.value)} style={{width: '150px'}} />
                                          <span className="text-muted">–</span>
                                          <input type="date" className="form-control form-control-sm" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} style={{width: '150px'}} />
                                      </div>
                                  </div>
                              </div>
                                  <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                                        <select className="form-select form-select-sm" value={compareMode} onChange={(e) => setCompareMode(e.target.value)} style={{width: 'auto'}}>
                                            <option value="previous_period">{t('vs. Vorperiode')}</option>
                                            <option value="previous_month">{t('vs. Vormonat')}</option>
                                            <option value="previous_year">{t('vs. Vorjahr')}</option>
                                        </select>
                                        {data && (
                                            <span className="text-muted fs-2">{data.period.start_comp} – {data.period.end_comp}</span>
                                        )}
                                    </div>
                          </div>
                      </div>
                  </div>

                  {loading && (
                      <div className="col-12">
                          <div className="card border-0 shadow-sm">
                              <div className="card-body text-center py-5">
                                  <div className="spinner-border text-primary" role="status"></div>
                                  <p className="mt-3 text-muted">{t('Dashboard wird geladen...')}</p>
                              </div>
                          </div>
                      </div>
                  )}

                  {!loading && data && (
                      <>
                          {kpiCards.map((kpi, index) => (
                              <div className="col-sm-6 col-xl-3 mb-4" key={index}>
                                  <div className="card border-0 shadow-sm h-100 mb-0">
                                      <div className="card-body">
                                          <div className="d-flex align-items-center justify-content-between mb-3">
                                              <div className={'rounded-circle d-flex align-items-center justify-content-center bg-light-' + kpi.color} style={{width: '48px', height: '48px'}}>
                                                  <i className={'ti ' + kpi.icon + ' fs-6 text-' + kpi.color}></i>
                                              </div>
                                              {renderTrend(kpi.value, kpi.prev, 'sm')}
                                          </div>
                                          <h3 className="fw-semibold mb-1">{kpi.value.toLocaleString('de-DE')}</h3>
                                          <p className="text-muted mb-0 fs-3">{kpi.label}</p>
                                      </div>
                                  </div>
                              </div>
                          ))}

                          {data.businesses && data.businesses.length > 0 && (
                              <div className="col-12">
                                  <div className="card border-0 shadow-sm">
                                      <div className="px-4 py-3 border-bottom">
                                          <h5 className="fw-semibold mb-0">{t('Standorte im Überblick')}</h5>
                                      </div>
                                      <div className="card-body p-0">
                                          <div className="table-responsive">
                                              <table className="table table-hover align-middle mb-0">
                                                  <thead>
                                                      <tr className="bg-light">
                                                          <th className="fw-semibold ps-4">{t('Standort')}</th>
                                                          <th className="text-end fw-semibold">{t('Impressionen')}</th>
                                                          <th className="text-end fw-semibold">{t('Website-Klicks')}</th>
                                                          <th className="text-end fw-semibold">{t('Anrufe')}</th>
                                                          <th className="text-end fw-semibold">{t('Wegbeschreibungen')}</th>
                                                          <th className="text-center pe-4" style={{width: '100px'}}></th>
                                                      </tr>
                                                  </thead>
                                                  <tbody>
                                                      {data.businesses.map((biz, index) => (
                                                          <tr key={index}>
                                                              <td className="ps-4">
                                                                  <Link to={'/businesses/' + biz.id} className="text-decoration-none">
                                                                      <div className="fw-semibold text-dark">{getTitle(biz)}</div>
                                                                      {getAddress(biz) && (
                                                                          <small className="text-muted">{getAddress(biz)}</small>
                                                                      )}
                                                                  </Link>
                                                              </td>
                                                              <td className="text-end">
                                                                  <div className="fw-semibold">{biz.impressions.toLocaleString('de-DE')}</div>
                                                                  <div>{renderTrend(biz.impressions, biz.impressions_prev, 'sm')}</div>
                                                              </td>
                                                              <td className="text-end">
                                                                  <div className="fw-semibold">{biz.clicks.toLocaleString('de-DE')}</div>
                                                                  <div>{renderTrend(biz.clicks, biz.clicks_prev, 'sm')}</div>
                                                              </td>
                                                              <td className="text-end">
                                                                  <div className="fw-semibold">{biz.calls.toLocaleString('de-DE')}</div>
                                                                  <div>{renderTrend(biz.calls, biz.calls_prev, 'sm')}</div>
                                                              </td>
                                                              <td className="text-end">
                                                                  <div className="fw-semibold">{biz.directions.toLocaleString('de-DE')}</div>
                                                                  <div>{renderTrend(biz.directions, biz.directions_prev, 'sm')}</div>
                                                              </td>
                                                              <td className="text-center pe-4">
                                                                  <Link to={'/businesses/' + biz.id + '/insights'} className="btn btn-sm btn-light-primary text-primary">
                                                                      <i className="ti ti-chart-bar me-1"></i>{t('Insights')}
                                                                  </Link>
                                                              </td>
                                                          </tr>
                                                      ))}
                                                  </tbody>
                                              </table>
                                          </div>
                                      </div>
                                  </div>
                              </div>
                          )}
                      </>
                  )}

                  {!loading && !data && (
                      <div className="col-12">
                          <div className="card border-0 shadow-sm">
                              <div className="card-body text-center py-5">
                                  <i className="ti ti-alert-circle fs-7 text-muted"></i>
                                  <h5 className="mt-3">{t('Keine Daten verfügbar')}</h5>
                                  <p className="text-muted">{t('Bitte verbinde zuerst einen Standort.')}</p>
                                  <Link to="/businesses/add" className="btn btn-primary">{t('Standort verbinden')}</Link>
                              </div>
                          </div>
                      </div>
                  )}
              </div>
          </>
      );
  }

  export default Dashboard;
  